import { useCallback, useEffect, useMemo, useRef } from 'react';

import { type GroupedSharesResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	buildShareConversationPreviewUrl,
	getOrgIdFromLocation,
} from '@atlassian/conversation-assistant-ui-components/ui/ShareConversationModal/buildShareConversationUrl';

import { useRovoSettingsConfig } from '../../../../common/utils/rovo-settings-config';

import { type SharedChatLink } from './sharedChatsTypes';

/**
 * Maximum number of shares the grouped-shares endpoint will return.
 *
 * The backend caps the response at this many shares and sets `hasMore` when it
 * truncates. There are no pagination parameters, so shares beyond this cap
 * cannot be listed at all.
 */
export const MAX_VISIBLE_SHARES = 1000;

export interface UseGroupedSharesProps extends AssistanceServiceInjectedParams {
	/**
	 * Whether to enable fetching. Typically driven by whether the tab/modal is
	 * currently visible.
	 */
	isEnabled: boolean;
}

export interface UseGroupedSharesReturn {
	/** The flattened list of shared-chat rows for the UI. */
	recentLinks: SharedChatLink[];
	/** Whether the grouped shares are currently loading. */
	isLoading: boolean;
	/**
	 * Whether the server truncated the response because the user owns more than
	 * `MAX_VISIBLE_SHARES` shares. The remaining shares cannot be fetched.
	 */
	hasMore: boolean;
	/** Retry fetching after an error. */
	onRetry: () => Promise<void>;
	/**
	 * Error from fetching, if any. A `404` (flag off / no org) is surfaced here
	 * and treated by the UI as "no shares".
	 */
	error?: ApiError | null;
	/**
	 * Delete the share(s) a row represents, then refresh the list.
	 *
	 * - `conversation` rows delete every snapshot for the conversation.
	 * - `prompt` (per-share) rows delete just that snapshot.
	 *
	 * Rejects if the delete request fails so the caller can surface an error and
	 * keep the row.
	 */
	onDeleteRow: (row: SharedChatLink) => Promise<void>;
}

/**
 * Derives the display label for a single share row. Prefers `shareName`, falling
 * back to the message id when it is not present.
 */
export function getShareRowLabel(
	share: GroupedSharesResponse['conversations'][number]['shares'][number],
): string {
	return share.shareName ?? share.messageId;
}

type GroupedShare = GroupedSharesResponse['conversations'][number]['shares'][number];

/**
 * Builds the owner preview URL directly from the conversation and boundary
 * message, without requiring the token-backed sender view.
 */
function buildSharePreviewUrl(share: GroupedShare, orgId?: string): string {
	return buildShareConversationPreviewUrl({
		conversationId: share.conversationId,
		messageId: share.messageId,
		orgId,
	});
}

/**
 * Flattens the hierarchical grouped-shares response into the flat row list the
 * UI renders.
 *
 * For each conversation group we emit:
 * - one `conversation` row labelled with the conversation name (falling back to
 *   the conversation id when the name is `null`), followed by
 * - one `prompt` row per share within that conversation.
 *
 * `orgId` scopes the per-share preview links to the sender's org. Callers should
 * pass the real org id (from config) and only fall back to the URL when it is
 * absent.
 *
 * Exported for testing.
 */
export function flattenGroupedShares(
	response: GroupedSharesResponse | null | undefined,
	orgId?: string,
): SharedChatLink[] {
	if (!response) {
		return [];
	}

	return response.conversations.flatMap((group) => {
		const conversationRow: SharedChatLink = {
			id: group.conversationId,
			title: group.conversationName ?? group.conversationId,
			href: '',
			type: 'conversation',
			conversationId: group.conversationId,
		};

		const shareRows: SharedChatLink[] = group.shares.map((share) => ({
			id: share.shareToken,
			title: getShareRowLabel(share),
			href: buildSharePreviewUrl(share, orgId),
			type: 'prompt',
			conversationId: group.conversationId,
			shareToken: share.shareToken,
		}));

		return [conversationRow, ...shareRows];
	});
}

export interface UseGroupedSharesInput
	extends
		Omit<UseGroupedSharesProps, 'assistanceServiceInjectedParams'>,
		AssistanceServiceInputParam {}

/**
 * Hook for fetching the current user's owned shares (grouped by conversation)
 * and mapping them to the flat row list rendered by the Shared chats tab.
 */
export const useGroupedShares: (props: UseGroupedSharesInput) => UseGroupedSharesReturn =
	createHookWithAssistanceService(
		({
			assistanceServiceInjectedParams,
			isEnabled,
		}: UseGroupedSharesProps): UseGroupedSharesReturn => {
			const { assistanceService, siteId } = assistanceServiceInjectedParams;

			// Prefer the real org id from the settings-modal config; fall back to
			// parsing it from the URL for hosts that don't provide one in context.
			const { orgId: configOrgId } = useRovoSettingsConfig();
			const orgId = configOrgId ?? getOrgIdFromLocation();

			const {
				data: groupedResponse,
				isFetching,
				refetch,
				error,
			} = useQuery<GroupedSharesResponse>({
				// Scope the cache key by site: the grouped-shares endpoint returns
				// results for the current org context, so keeping siteId in the key
				// prevents a hook mounted in another site's context from reusing a
				// stale cache entry.
				queryKey: ['grouped-shares', siteId],
				enabled: isEnabled,
				queryFn: assistanceService.getGroupedConversationShares,
			});

			// Refetch when the tab is reopened (isEnabled transitions false → true).
			// Skip the initial mount since useQuery handles the first fetch.
			const hasInitialized = useRef(false);
			useEffect(() => {
				if (!hasInitialized.current) {
					hasInitialized.current = true;
					return;
				}
				if (isEnabled) {
					refetch();
				}
			}, [isEnabled, refetch]);

			const recentLinks = useMemo(
				() => flattenGroupedShares(groupedResponse, orgId),
				[groupedResponse, orgId],
			);

			const onRetry = useCallback(async () => {
				await refetch();
			}, [refetch]);

			const onDeleteRow = useCallback(
				async (row: SharedChatLink) => {
					if (row.type === 'conversation') {
						await assistanceService.deleteAllSharesForConversation(row.conversationId);
					} else if (row.shareToken) {
						await assistanceService.deleteConversationShare(row.shareToken);
					} else {
						return;
					}
					// Re-fetch so the list reflects the server state (a conversation
					// delete removes every child share row, not just the clicked one).
					await refetch();
				},
				[assistanceService, refetch],
			);

			return {
				recentLinks,
				hasMore: groupedResponse?.hasMore ?? false,
				isLoading: isFetching,
				onRetry,
				error,
				onDeleteRow,
			};
		},
	);
